import {Subscription} from "rxjs";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';

import {NgSelectComponent} from "@ng-select/ng-select";
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbInputDatepicker
} from "@ng-bootstrap/ng-bootstrap";

import {
  AlertService,
  CustomDateParserFormatter,
  CustomDatepickerI18n,
  DatepickerService,
  I18n
} from "@numizmat/services/common";
import {
  AdAutoRenewOptionApiService,
  AdAutoRenewOptionStateService,
  AdBidExtensionTimeOptionApiService,
  AdBidExtensionTimeOptionStateService
} from "@numizmat/services/nomenclature";

import {AdStatus, AdType} from "@numizmat/enumerations";
import {HOURS_OPTIONS, MINUTES_OPTIONS} from "@numizmat/constants";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {AdAutoRenewOptionResponse, AdBidExtensionTimeOptionResponse} from "@numizmat/services-models/nomenclature";

@Component({
    selector: 'nz-ad-form-step-02',
    imports: [
        ReactiveFormsModule,
        NgbInputDatepicker,
        NgSelectComponent
    ],
    templateUrl: './ad-form-step-02.component.html',
    styleUrl: './ad-form-step-02.component.scss',
    providers: [I18n,
        { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
    ]
})
export class AdFormStep02Component implements OnInit, OnDestroy {
  @Input() form!: FormGroup;

  private calendar: NgbCalendar = inject(NgbCalendar);
  private alertService: AlertService = inject(AlertService);
  private datepickerService: DatepickerService = inject(DatepickerService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);
  private adAutoRenewOptionApiService: AdAutoRenewOptionApiService = inject(AdAutoRenewOptionApiService);
  private adAutoRenewOptionStateService: AdAutoRenewOptionStateService = inject(AdAutoRenewOptionStateService);
  private adBidExtensionTimeOptionApiService: AdBidExtensionTimeOptionApiService = inject(AdBidExtensionTimeOptionApiService);
  private adBidExtensionTimeOptionStateService: AdBidExtensionTimeOptionStateService = inject(AdBidExtensionTimeOptionStateService);

  private subs: Subscription = new Subscription();

  protected readonly AdType = AdType;
  protected readonly hourOptions = HOURS_OPTIONS;
  protected readonly minutesOptions = MINUTES_OPTIONS;

  protected adStatus?: AdStatus;
  protected startDateMinDate!: NgbDateStruct;
  protected startDateMaxDate!: NgbDateStruct;
  protected endDateMinDate!: NgbDateStruct;
  protected endDateMaxDate!: NgbDateStruct;
  protected adAutoRenewOptions!: AdAutoRenewOptionResponse[];
  protected adBidExtensionTimeOptions!: AdBidExtensionTimeOptionResponse[];

  public ngOnInit(): void {
    this.getAllAdAutoRenewOptions();
    this.getAllAdBidExtensionTimeOptions();
    this.createAdTypeValueChangesSubscription();
    this.createHasBuyNowPriceValueChangesSubscription();
    this.createHasReservedPriceValueChangesSubscription();
    this.createHasAdBidExtensionTimeOptionIdValueChangesSubscription();
    this.createStartDateValueChangesSubscription();
    this.createHasAdAutoRenewAfterExpiredIdValueChangesSubscription();
    this.getPrivateAdDetails();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected showFixedPriceAdTypeInfo() {
    this.alertService.showFixedPriceAdTypeInfo();
  }

  protected showAllowOfferInfo(): void {
    this.alertService.showAllowOfferInfo();
  }

  protected showAuctionAdTypeInfo() {
    this.alertService.showAuctionAdTypeInfo();
  }

  protected showBuyNowPriceInfo() {
    this.alertService.showBuyNowPriceInfo();
  }

  protected showReservedPriceInfo() {
    this.alertService.showReservedPriceInfo();
  }

  protected showBidExtensionTimeInfo() {
    this.alertService.showBidExtensionTimeInfo();
  }

  protected showAutoRenewAfterExpiredInfo() {
    this.alertService.showAutoRenewAfterExpiredInfo();
  }

  private getPrivateAdDetails(): void {
    const subscription: Subscription = this.adDetailStateService.getPrivateAdDetails().subscribe(adDetails => {
      if (!adDetails) {
        return;
      }

      this.adStatus = adDetails.data?.adStatus;

      if (!!adDetails.data && adDetails.mode !== 'create') {
        this.adType?.setValue(adDetails.data.adType);
        this.fixedPrice?.setValue(adDetails.data.fixedPrice);
        this.isOfferSubmissionAllowed?.setValue(adDetails.data.isOfferSubmissionAllowed);
        this.startingPrice?.setValue(adDetails.data.startingPrice);
        this.bidIncrement?.setValue(adDetails.data.bidIncrement);
        this.hasBuyNowPrice?.setValue(!!adDetails.data.buyNowPrice);
        this.buyNowPrice?.setValue(adDetails.data.buyNowPrice);
        this.hasReservedPrice?.setValue(!!adDetails.data.reservedPrice);
        this.reservedPrice?.setValue(adDetails.data.reservedPrice);
        this.hasAdBidExtensionTimeOptionId?.setValue(!!adDetails.data.adBidExtensionTimeOptionId);
        this.adBidExtensionTimeOptionId?.setValue(adDetails.data.adBidExtensionTimeOptionId);

        if (!!adDetails.data.adAutoRenewAfterExpiredId) {
          this.hasAdAutoRenewAfterExpiredId?.setValue(true);
          this.adAutoRenewAfterExpiredId?.setValue(adDetails.data.adAutoRenewAfterExpiredId);
        }

        if (!!this.adStatus && this.adStatus === AdStatus.CompletedWithoutDeal) {
          this.hasAdAutoRenewAfterExpiredId?.disable();
        }

        if (adDetails.mode === 'update') {
          this.adType?.disable();
          this.startingPrice?.disable();
          this.bidIncrement?.disable();
          this.hasBuyNowPrice?.disable();
          this.buyNowPrice?.disable();
          this.hasReservedPrice?.disable();
          this.reservedPrice?.disable();
          this.hasAdBidExtensionTimeOptionId?.disable();
          this.adBidExtensionTimeOptionId?.disable();

          if (adDetails.data.adType === AdType.Auction) {
            this.fixedPrice?.disable();
            this.isOfferSubmissionAllowed?.disable();
          }

          this.startDateMinDate = this.datepickerService.parseDateToNgbDateStruct(adDetails.data.startDateTime)
          this.startDateMaxDate = this.startDateMinDate;
          this.endDateMinDate = this.datepickerService.parseDateToNgbDateStruct(adDetails.data.endDateTime)
          this.endDateMaxDate = this.endDateMinDate;

          this.startDate?.setValue(this.datepickerService.parseDateToNgbDateStruct(adDetails.data.startDateTime));
          this.endDate?.setValue(this.datepickerService.parseDateToNgbDateStruct(adDetails.data.endDateTime));
          const startDate = new Date(adDetails.data.startDateTime);
          this.startHour?.setValue(startDate.getHours());
          this.startMinutes?.setValue(startDate.getMinutes());
          const endDate = new Date(adDetails.data.endDateTime);
          this.endHour?.setValue(endDate.getHours());
          this.endMinutes?.setValue(endDate.getMinutes());

          this.startDate?.disable();
          this.endDate?.disable();
          this.startHour?.disable();
          this.startMinutes?.disable();
          this.endHour?.disable();
          this.endMinutes?.disable();
        }
      }

      if (adDetails.mode === 'create' || adDetails.mode === 'create-similar') {
        const date = new Date();
        this.startHour?.setValue(date.getHours());
        this.startMinutes?.setValue(date.getMinutes());
        this.endHour?.setValue(date.getHours());
        this.endMinutes?.setValue(date.getMinutes());
        this.startDate?.setValue(this.calendar.getToday());
        this.initializeMinAndMaxDate();
      }
    });
    this.subs.add(subscription);
  }

  private initializeMinAndMaxDate(): void {
    this.startDateMinDate = this.calendar.getToday();
    this.startDateMaxDate = this.calendar.getNext(this.calendar.getToday(), 'm', 6);
    this.endDateMinDate = this.startDateMinDate;
    this.endDateMaxDate = this.startDateMaxDate;
  }

  private getAllAdAutoRenewOptions(): void {
    const subscription: Subscription = this.adAutoRenewOptionStateService.getAllAdAutoRenewOptions().subscribe({
      next: adAutoRenewOptions => {
        if (!!adAutoRenewOptions) {
          this.adAutoRenewOptions = adAutoRenewOptions;
        } else {
          this.adAutoRenewOptionApiService.getAllAdAutoRenewOptions().subscribe({
            next: response => {
              this.adAutoRenewOptionStateService.setAllAdAutoRenewOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription)
  }

  private getAllAdBidExtensionTimeOptions(): void {
    const subscription: Subscription = this.adBidExtensionTimeOptionStateService.getAllAdBidExtensionTimeOptions().subscribe({
      next: adBidExtensionTimeOptions => {
        if (!!adBidExtensionTimeOptions) {
          this.adBidExtensionTimeOptions = adBidExtensionTimeOptions;
        } else {
          this.adBidExtensionTimeOptionApiService.getAllAdBidExtensionTimeOptions().subscribe({
            next: response => {
              this.adBidExtensionTimeOptionStateService.setAllAdBidExtensionTimeOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private createAdTypeValueChangesSubscription(): void {
    const valueChangesSubscription = this.adType?.valueChanges.subscribe({
      next: (value: AdType) => {
        if (value === AdType.DirectSale) {
          this.fixedPrice?.enable();
          this.startingPrice?.disable();
          this.bidIncrement?.disable();
          this.buyNowPrice?.disable();
          this.reservedPrice?.disable();
          this.adBidExtensionTimeOptionId?.disable();
        } else if (value === AdType.Auction) {
          this.fixedPrice?.disable();
          this.startingPrice?.enable();
          this.bidIncrement?.enable();

          if (this.hasBuyNowPrice?.value) {
            this.buyNowPrice?.enable();
          }
          if (this.hasReservedPrice?.value) {
            this.reservedPrice?.enable();
          }
          if (this.hasAdBidExtensionTimeOptionId?.value) {
            this.adBidExtensionTimeOptionId?.enable();
          }
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  private createHasBuyNowPriceValueChangesSubscription(): void {
    const valueChangesSubscription = this.hasBuyNowPrice?.valueChanges.subscribe({
      next: (value: boolean) => {
        if (value) {
          this.buyNowPrice?.enable();
        } else {
          this.buyNowPrice?.setValue(null);
          this.buyNowPrice?.disable();
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  private createHasReservedPriceValueChangesSubscription(): void {
    const valueChangesSubscription = this.hasReservedPrice?.valueChanges.subscribe({
      next: (value: boolean) => {
        if (value) {
          this.reservedPrice?.enable();
        } else {
          this.reservedPrice?.setValue(null);
          this.reservedPrice?.disable();
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  private createHasAdBidExtensionTimeOptionIdValueChangesSubscription(): void {
    const valueChangesSubscription = this.hasAdBidExtensionTimeOptionId?.valueChanges.subscribe({
      next: (value: boolean) => {
        if (value) {
          this.adBidExtensionTimeOptionId?.enable();
        } else {
          this.adBidExtensionTimeOptionId?.setValue(null);
          this.adBidExtensionTimeOptionId?.disable();
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  private createHasAdAutoRenewAfterExpiredIdValueChangesSubscription(): void {
    const valueChangesSubscription = this.hasAdAutoRenewAfterExpiredId?.valueChanges.subscribe({
      next: (value: boolean) => {
        if (value) {
          if (!!this.adStatus && this.adStatus === AdStatus.CompletedWithoutDeal) {
            this.adAutoRenewAfterExpiredId?.disable();
          } else {
            this.adAutoRenewAfterExpiredId?.enable();
          }
        } else {
          this.adAutoRenewAfterExpiredId?.setValue(null);
          this.adAutoRenewAfterExpiredId?.disable();
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  private createStartDateValueChangesSubscription() {
    const valueChangesSubscription = this.startDate?.valueChanges.subscribe({
      next: (value: NgbDate) => {
        if (!!value) {
          this.endDate?.setValue(this.calendar.getNext(value, 'd', 10));
          this.endDateMinDate = this.calendar.getNext(value, 'd', 1);
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  get adType() {
    return this.form.get('adType');
  }

  get fixedPrice() {
    return this.form.get('fixedPrice');
  }

  get isOfferSubmissionAllowed() {
    return this.form.get('isOfferSubmissionAllowed');
  }

  get startingPrice() {
    return this.form.get('startingPrice');
  }

  get bidIncrement() {
    return this.form.get('bidIncrement');
  }

  get hasBuyNowPrice() {
    return this.form.get('hasBuyNowPrice');
  }

  get buyNowPrice() {
    return this.form.get('buyNowPrice');
  }

  get hasReservedPrice() {
    return this.form.get('hasReservedPrice');
  }

  get reservedPrice() {
    return this.form.get('reservedPrice');
  }

  get hasAdBidExtensionTimeOptionId() {
    return this.form.get('hasAdBidExtensionTimeOptionId');
  }

  get adBidExtensionTimeOptionId() {
    return this.form.get('adBidExtensionTimeOptionId');
  }

  get hasAdAutoRenewAfterExpiredId() {
    return this.form.get('hasAdAutoRenewAfterExpiredId');
  }

  get adAutoRenewAfterExpiredId() {
    return this.form.get('adAutoRenewAfterExpiredId');
  }

  get startDate() {
    return this.form.get('startDate');
  }

  get startHour() {
    return this.form.get('startHour');
  }

  get startMinutes() {
    return this.form.get('startMinutes');
  }

  get endDate() {
    return this.form.get('endDate');
  }

  get endHour() {
    return this.form.get('endHour');
  }

  get endMinutes() {
    return this.form.get('endMinutes');
  }
}
